// 2D/3D split search — client for the federated query.
//
// The response shape is the contract the split view is built on: two
// independent lists, plus the canonical tags they had in common. Nothing
// here merges them (docs/2d-3d-search.md).
import { url, tokenRef } from "api";

const auth = () => {
  const t = localStorage.getItem(tokenRef);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const json = async (res) => {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(body.err || `HTTP ${res.status}`);
    // A 403 names the key it wants (sheetSearchView on this tab).
    e.status = res.status; e.missingPermission = body.missingPermission || "";
    throw e;
  }
  return body;
};

/**
 * One query, both worlds.
 * -> { query, tags, aliasedTo, intersection,
 *      '3d': [...], '2d': [...], counts: {'3d','2d',agreed} }
 */
// The glance link's structured target (room, roomName, level, levelName,
// uniqueId): looked up exactly and listed first; q still runs underneath.
export const TARGET_KEYS = ["roomId", "itemKey", "room", "roomName", "level", "levelName", "uniqueId"];

// The glance link's glance=room:<id> | item:<level:system:x,y>, resolved
// from the latest building data (root-04's GET /glance/locate): label,
// level, box in building feet, model uniqueId, and each source sheet with
// its box in page percent when that page is registered.
export const locateGlance = async (projectId, glance) => {
  const str = String(glance || "");
  const cut = str.indexOf(":");
  const kind = str.slice(0, cut) === "item" ? "item" : "room";
  const id = str.slice(cut + 1);
  return json(await fetch(`${url}/projects/${projectId}/glance/locate?${kind}=${encodeURIComponent(id)}`, { headers: auth() }));
};

export const searchAnchors = async (projectId, q, limit = 50, scope = null, target = null) => {
  // scope: { docId, page? } confines the 2D half to one sheet. The 3D
  // half is never scoped - a sheet is not a thing the model has.
  const qs = [
    `q=${encodeURIComponent(q)}`,
    `limit=${limit}`,
    scope && scope.docId ? `docId=${encodeURIComponent(scope.docId)}` : "",
    scope && scope.docId && Number.isInteger(scope.page) ? `page=${scope.page}` : "",
    ...TARGET_KEYS.map((k) => (target && target[k] ? `${k}=${encodeURIComponent(target[k])}` : "")),
  ].filter(Boolean).join("&");
  return json(await fetch(`${url}/projects/${projectId}/search?${qs}`, { headers: auth() }));
};

/**
 * The elements behind 3D hits, for the model pane.
 * -> { uids: [...], byAnchor: { anchorId: [start, end) }, capped }
 */
export const expandAnchorUids = async (projectId, ids, limit) =>
  json(await fetch(`${url}/projects/${projectId}/anchors/uids`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth() },
    body: JSON.stringify({ ids, ...(limit ? { limit } : {}) }),
  }));

/** Sheets that carry anchors, with page size — what the 2D pane offers. */
export const listAnchorSheets = async (projectId) =>
  json(await fetch(`${url}/projects/${projectId}/sheets`, { headers: auth() }));

/** One element's anchor plus the query that finds it in the other world. */
export const elementAnchor = async (projectId, uniqueId) =>
  json(await fetch(
    `${url}/projects/${projectId}/anchors/element/${encodeURIComponent(uniqueId)}`,
    { headers: auth() }
  ));

/**
 * A rendered sheet page as an object URL.
 *
 * Fetched rather than pointed at with <img src>, because the endpoint is
 * behind the JWT and an image tag cannot carry the header. The caller
 * owns the returned URL and must revokeObjectURL it.
 */
// `ver` names the file the page is of (GET /sheets gives it). The image is
// cached a day under its URL, so without it a revised sheet kept showing
// the drawing it replaced under the new highlights.
export const sheetPageObjectUrl = async (projectId, docId, page, width = 2400, ver = "") => {
  const res = await fetch(
    `${url}/projects/${projectId}/sheets/${docId}/page/${page}.png?w=${width}${ver ? `&v=${encodeURIComponent(ver)}` : ""}`,
    { headers: auth() }
  );
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { msg = (await res.json()).err || msg; } catch (e) { /* not json */ }
    throw new Error(msg);
  }
  return URL.createObjectURL(await res.blob());
};

export const listAliases = async (projectId) =>
  json(await fetch(`${url}/projects/${projectId}/anchors/aliases`, { headers: auth() }));

export const saveAlias = async (projectId, body) =>
  json(await fetch(`${url}/projects/${projectId}/anchors/aliases`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth() },
    body: JSON.stringify(body),
  }));

export const deleteAlias = async (projectId, id) =>
  json(await fetch(`${url}/projects/${projectId}/anchors/aliases/${id}`, {
    method: "DELETE", headers: auth(),
  }));

export const reindexModel = async (projectId) =>
  json(await fetch(`${url}/projects/${projectId}/anchors/reindex`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth() },
    body: "{}",
  }));

// Every drawing on the project, to the index queue; answers at once with
// how many were queued (utils/sheetIndexCatchUp queueProject).
export const queueAllSheets = async (projectId) =>
  json(await fetch(`${url}/projects/${projectId}/sheets/ingest-all`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth() },
    body: JSON.stringify({ queue: true }),
  }));

export const ingestAllSheets = async (projectId, limit = 25) =>
  json(await fetch(`${url}/projects/${projectId}/sheets/ingest-all`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth() },
    body: JSON.stringify({ limit }),
  }));
